import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import toast from 'react-hot-toast';
import {
  isIndiaCountry,
  isValidIndianPincode,
  fetchIndiaPincodeDetails,
  fetchIndiaServiceability,
  normalizePincode
} from '../utils/indiaPincode';
import { 
  CreditCardIcon,
  TruckIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  DevicePhoneMobileIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart, fetchCartItems } = useCart();
  const { user, isAuthenticated, getToken } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const CHECKOUT_ADDRESS_STORAGE_PREFIX = 'checkout_address_v1:';
  const getCheckoutAddressStorageKey = (userId) => `${CHECKOUT_ADDRESS_STORAGE_PREFIX}${userId || 'guest'}`;
  const readStoredCheckoutAddress = (userId) => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem(getCheckoutAddressStorageKey(userId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };
  const writeStoredCheckoutAddress = (userId, payload) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(getCheckoutAddressStorageKey(userId), JSON.stringify(payload));
    } catch {
      // ignore
    }
  };

  const [formData, setFormData] = useState(() => {
    const stored = readStoredCheckoutAddress(user?.id);
    return {
      firstName: user?.first_name || stored?.firstName || '',
      lastName: user?.last_name || stored?.lastName || '',
      email: user?.email || stored?.email || '',
      phone: stored?.phone || '',
      address: stored?.address || '',
      city: stored?.city || '',
      state: stored?.state || '',
      district: stored?.district || '',
      zipCode: stored?.zipCode || '',
      country: stored?.country || '',
      landmark: stored?.landmark || '',
      manualLocationEdit: false,
      paymentMethod: 'upi',
      billingSameAsShipping: true,
      selectedAddressId: null,
      saveAddress: true,
      useNewAddress: false // Track if user wants to add new address
    };
  });

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [pincodeLookup, setPincodeLookup] = useState({ loading: false, error: '', data: null });
  const [serviceability, setServiceability] = useState({ loading: false, message: '', configured: false, serviceable: true, codAvailable: true });
  const [promoCode, setPromoCode] = useState('');
  const [promoState, setPromoState] = useState({ loading: false, applied: null, error: '' });
  const [shippingConfig, setShippingConfig] = useState({ enabled: true, flatCharge: 500, freeOver: 5000 });

  // Determine available payment methods based on country
  const getAvailablePaymentMethods = () => {
    return [
      { value: 'upi', label: 'UPI', icon: DevicePhoneMobileIcon },
      { value: 'card', label: 'Credit/Debit Card', icon: CreditCardIcon },
      { value: 'paylater', label: 'Pay Later', icon: ClockIcon },
      { value: 'cod', label: 'Cash on Delivery', icon: TruckIcon }
    ];
  };

  const availablePaymentMethods = getAvailablePaymentMethods();

  const parseJsonResponseSafely = async (res) => {
    const text = await res.text();
    let json = null;

    try {
      json = text ? JSON.parse(text) : null;
    } catch (_err) {
      json = null;
    }

    return { json, text };
  };

  const getApiErrorMessage = (res, parsed, fallbackMessage) => {
    if (parsed?.json?.message) return parsed.json.message;
    if (parsed?.json?.error) return parsed.json.error;

    const contentType = String(res.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('application/json')) {
      return 'Server returned a non-JSON response. Please refresh and try again.';
    }

    return fallbackMessage;
  };

  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);

  // When user profile loads, gently fill missing name/email.
  // Also restore last-used address for this user (per-origin) if present.
  useEffect(() => {
    const stored = readStoredCheckoutAddress(user?.id);
    setFormData((prev) => {
      const next = { ...prev };

      if (!next.firstName && user?.first_name) next.firstName = user.first_name;
      if (!next.lastName && user?.last_name) next.lastName = user.last_name;
      if (!next.email && user?.email) next.email = user.email;

      if (stored) {
        if (!next.firstName && stored.firstName) next.firstName = stored.firstName;
        if (!next.lastName && stored.lastName) next.lastName = stored.lastName;
        if (!next.email && stored.email) next.email = stored.email;
        if (!next.phone && stored.phone) next.phone = stored.phone;
        if (!next.address && stored.address) next.address = stored.address;
        if (!next.city && stored.city) next.city = stored.city;
        if (!next.state && stored.state) next.state = stored.state;
        if (!next.district && stored.district) next.district = stored.district;
        if (!next.zipCode && stored.zipCode) next.zipCode = stored.zipCode;
        if (!next.country && stored.country) next.country = stored.country;
        if (!next.landmark && stored.landmark) next.landmark = stored.landmark;
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.first_name, user?.last_name, user?.email]);

  // Persist last-used checkout address locally (per-origin), so repeat checkouts don't re-prompt.
  useEffect(() => {
    const userId = user?.id;
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      district: formData.district,
      zipCode: formData.zipCode,
      country: formData.country,
      landmark: formData.landmark
    };

    const timer = setTimeout(() => {
      writeStoredCheckoutAddress(userId, payload);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phone,
    formData.address,
    formData.city,
    formData.state,
    formData.district,
    formData.zipCode,
    formData.country,
    formData.landmark
  ]);

  useEffect(() => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  // Load shipping config (public settings endpoint)
  useEffect(() => {
    let cancelled = false;
    const loadShippingConfig = async () => {
      try {
        const response = await fetch('/api/settings');
        const data = await response.json();

        const value = data?.data?.settings?.shipping_config?.value;
        if (!cancelled && value && typeof value === 'object') {
          setShippingConfig(prev => ({
            ...prev,
            enabled: value.enabled !== undefined ? Boolean(value.enabled) : prev.enabled,
            flatCharge: Number.isFinite(Number(value.flatCharge)) ? Number(value.flatCharge) : prev.flatCharge,
            freeOver: Number.isFinite(Number(value.freeOver)) ? Number(value.freeOver) : prev.freeOver
          }));
        }
      } catch (e) {
        // Keep defaults
      }
    };

    loadShippingConfig();
    return () => { cancelled = true; };
  }, []);

  // Load saved addresses
  useEffect(() => {
    const loadSavedAddresses = async () => {
      if (!isAuthenticated) return;
      
      try {
        const token = getToken(false); // E-commerce token
        if (!token) {
          console.warn('No token available');
          return;
        }
        
        const API_BASE = process.env.REACT_APP_API_URL || '';
        
        const response = await fetch(`${API_BASE}/api/addresses`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.status === 401) {
          // Token is invalid - user needs to re-login
          console.error('Token is invalid, please logout and login again');
          setSavedAddresses([]);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSavedAddresses(data.data || []);

            // If user has no saved addresses yet, ensure we show the address form
            if (!data.data || data.data.length === 0) {
              setFormData(prev => ({
                ...prev,
                selectedAddressId: null,
                useNewAddress: true
              }));
              return;
            }
            
            // Auto-select default address if available
            const defaultAddress = data.data.find(addr => addr.is_default);
            if (defaultAddress) {
              setFormData(prev => ({
                ...prev,
                selectedAddressId: defaultAddress.id,
                useNewAddress: false, // Use saved address, don't show form fields
                firstName: defaultAddress.first_name || prev.firstName,
                lastName: defaultAddress.last_name || prev.lastName,
                email: defaultAddress.email || prev.email,
                phone: defaultAddress.phone || prev.phone,
                address: defaultAddress.street || prev.address,
                city: defaultAddress.city || prev.city,
                state: defaultAddress.state || prev.state,
                district: defaultAddress.district || prev.district,
                zipCode: defaultAddress.zip_code || prev.zipCode,
                country: defaultAddress.country || prev.country,
                landmark: defaultAddress.landmark || prev.landmark
              }));
            } else if (data.data.length > 0) {
              // If no default, use first address
              const firstAddress = data.data[0];
              setFormData(prev => ({
                ...prev,
                selectedAddressId: firstAddress.id,
                useNewAddress: false,
                firstName: firstAddress.first_name || prev.firstName,
                lastName: firstAddress.last_name || prev.lastName,
                email: firstAddress.email || prev.email,
                phone: firstAddress.phone || prev.phone,
                address: firstAddress.street || prev.address,
                city: firstAddress.city || prev.city,
                state: firstAddress.state || prev.state,
                district: firstAddress.district || prev.district,
                zipCode: firstAddress.zip_code || prev.zipCode,
                country: firstAddress.country || prev.country,
                landmark: firstAddress.landmark || prev.landmark
              }));
            }
          }
        } else {
          console.error('Failed to load addresses:', response.status);
          setSavedAddresses([]);
        }
      } catch (error) {
        console.error('Error loading saved addresses:', error);
        setSavedAddresses([]);
      }
    };

    loadSavedAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle saved address selection
  const handleAddressSelect = (addressId) => {
    const address = savedAddresses.find(addr => addr.id === parseInt(addressId));
    if (address) {
      setFormData(prev => ({
        ...prev,
        selectedAddressId: address.id,
        useNewAddress: false, // Hide form fields, use saved address
        firstName: address.first_name || prev.firstName,
        lastName: address.last_name || prev.lastName,
        email: address.email || prev.email,
        phone: address.phone || prev.phone,
        address: address.street || prev.address,
        city: address.city || prev.city,
        state: address.state || prev.state,
        district: address.district || prev.district,
        zipCode: address.zip_code || prev.zipCode,
        country: address.country || prev.country,
        landmark: address.landmark || prev.landmark
      }));
    }
  };

  // Handle new address (show form fields)
  const handleNewAddress = () => {
    setFormData(prev => ({
      ...prev,
      selectedAddressId: null,
      useNewAddress: true // Show form fields for new address
    }));
  };

  // India pincode lookup + autofill (only for new address entry)
  useEffect(() => {
    const usingNewAddress = formData.useNewAddress || savedAddresses.length === 0;
    if (!usingNewAddress) return;

    const apiBase = process.env.REACT_APP_API_URL || '';
    const isIndia = isIndiaCountry(formData.country);
    const pincode = normalizePincode(formData.zipCode);

    if (!isIndia) {
      setPincodeLookup({ loading: false, error: '', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true, codAvailable: true });
      return;
    }

    if (!pincode) {
      setPincodeLookup({ loading: false, error: '', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true, codAvailable: true });
      return;
    }

    if (!isValidIndianPincode(pincode)) {
      setPincodeLookup({ loading: false, error: 'Enter a valid 6-digit pincode', data: null });
      setServiceability({ loading: false, message: '', configured: false, serviceable: true, codAvailable: true });
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setPincodeLookup(prev => ({ ...prev, loading: true, error: '' }));
        const data = await fetchIndiaPincodeDetails({ apiBase, pincode });
        if (cancelled) return;
        setPincodeLookup({ loading: false, error: '', data });

        if (!formData.manualLocationEdit) {
          const firstOffice = data?.postOffices?.[0];
          setFormData(prev => ({
            ...prev,
            state: data.state || prev.state,
            district: data.district || prev.district,
            city: prev.city || firstOffice?.name || ''
          }));
        }

        setServiceability(prev => ({ ...prev, loading: true, message: '' }));
        const svc = await fetchIndiaServiceability({ apiBase, pincode });
        if (cancelled) return;
        setServiceability({
          loading: false,
          configured: !!svc?.configured,
          serviceable: svc?.serviceable !== false,
          codAvailable: svc?.codAvailable !== false,
          message: svc?.configured && svc?.serviceable === false
            ? 'This pincode may be out of delivery range.'
            : ''
        });
      } catch (e) {
        if (cancelled) return;
        setPincodeLookup({ loading: false, error: e.message || 'Failed to lookup pincode', data: null });
        setServiceability({ loading: false, message: '', configured: false, serviceable: true, codAvailable: true });
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country, formData.zipCode, formData.manualLocationEdit, formData.useNewAddress, savedAddresses.length]);

  const validateForm = () => {
    const newErrors = {};

    const usingNewAddress = formData.useNewAddress || savedAddresses.length === 0;

    // Only validate address fields if using new address
    if (usingNewAddress) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city.trim()) newErrors.city = isIndiaCountry(formData.country) ? 'Locality is required' : 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (isIndiaCountry(formData.country) && !formData.district.trim()) newErrors.district = 'District is required';
      if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip code is required';
      if (!formData.country.trim()) newErrors.country = 'Country is required';
    } else {
      // If using saved address, ensure one is selected
      if (!formData.selectedAddressId) {
        newErrors.selectedAddressId = 'Please select an address';
      }
    }

    if (!['upi', 'card', 'paylater', 'cod'].includes(String(formData.paymentMethod || '').toLowerCase())) {
      newErrors.paymentMethod = 'Please select a payment method';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') return reject(new Error('Browser environment required'));
      if (window.Razorpay) return resolve(true);

      const existing = document.querySelector('script[data-razorpay-checkout="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true));
        existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay')));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.dataset.razorpayCheckout = 'true';
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setProcessing(true);

    let createdOrderId = null;

    try {
      const token = getToken(false); // E-commerce token
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const selectedMethod = String(formData.paymentMethod || '').toLowerCase();
      const usingNewAddress = formData.useNewAddress || savedAddresses.length === 0;

      // Create order
      // If using saved address, get address details from savedAddresses
      let shippingAddressData;
      if (formData.selectedAddressId && !usingNewAddress) {
        const selectedAddress = savedAddresses.find(addr => addr.id === formData.selectedAddressId);
        if (selectedAddress) {
          shippingAddressData = {
            street: selectedAddress.street,
            landmark: selectedAddress.landmark,
            city: selectedAddress.city,
            state: selectedAddress.state,
            district: selectedAddress.district,
            zip_code: selectedAddress.zip_code,
            country: selectedAddress.country,
            first_name: selectedAddress.first_name,
            last_name: selectedAddress.last_name,
            phone: selectedAddress.phone,
            email: selectedAddress.email || formData.email
          };
        }
      } else {
        // Use form data for new address
        shippingAddressData = {
          street: formData.address,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          district: formData.district,
          zip_code: formData.zipCode,
          country: formData.country,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          email: formData.email
        };
      }

      const orderData = {
        shipping_address: shippingAddressData,
        payment_method: selectedMethod,
        promo_code: promoState.applied?.code || null,
        // Save address when user is entering a new one (including first-time checkout)
        save_address: usingNewAddress && formData.saveAddress,
        address_id: formData.selectedAddressId && !usingNewAddress ? formData.selectedAddressId : null
      };

      console.log('🛍️ [Checkout] Creating order:', orderData);
      const response = await fetch(`${API_BASE}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      console.log('🛍️ [Checkout] Order response status:', response.status);
      const parsedOrder = await parseJsonResponseSafely(response);
      const data = parsedOrder.json;
      console.log('🛍️ [Checkout] Order response data:', data);

      if (!response.ok || !data?.success) {
        const errMsg = getApiErrorMessage(response, parsedOrder, 'Failed to place order');
        console.error('❌ [Checkout] Order creation failed:', errMsg, parsedOrder.text);
        toast.error(errMsg);
        return;
      }

      // Handle both response formats: data.data.order.id or data.data.id or data.order.id
      const orderId = data.data?.order?.id || data.data?.id || data.order?.id;
      createdOrderId = orderId;
      console.log('🛍️ [Checkout] Order created with ID:', orderId);

      if (!orderId) {
        console.error('❌ [Checkout] Order ID not found in response:', data);
        toast.error('Order created but could not get order ID');
        return;
      }

      if (selectedMethod === 'cod') {
        toast.success('Order placed successfully! 🎉', { duration: 5000 });
        await clearCart();
        navigate(`/orders/${orderId}`);
        return;
      }

      // UPI/Card/Pay Later → Razorpay popup
      toast.success('Opening secure payment…', { duration: 3000 });

      const rpOrderRes = await fetch(`${API_BASE}/api/payments/razorpay/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order_id: orderId })
      });
      const parsedRpOrder = await parseJsonResponseSafely(rpOrderRes);
      const rpOrderJson = parsedRpOrder.json;
      if (!rpOrderRes.ok || !rpOrderJson?.success) {
        throw new Error(getApiErrorMessage(rpOrderRes, parsedRpOrder, 'Failed to create payment order'));
      }

      if (rpOrderJson?.data?.alreadyPaid) {
        navigate(`/orders/${orderId}`);
        return;
      }

      const { keyId, razorpayOrderId, amount, currency } = rpOrderJson.data;

      await loadRazorpayScript();
      if (!window.Razorpay) {
        throw new Error('Razorpay checkout failed to load');
      }

      const customerName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();

      const methodConfig = selectedMethod === 'upi'
        ? { upi: true, card: false, netbanking: false, wallet: false, emi: false, paylater: false }
        : selectedMethod === 'card'
          ? { upi: false, card: true, netbanking: false, wallet: false, emi: false, paylater: false }
          : selectedMethod === 'paylater'
            ? { upi: false, card: false, netbanking: false, wallet: false, emi: false, paylater: true }
            : undefined;

      const displayBlock = selectedMethod === 'upi'
        ? {
            blocks: {
              upi: {
                name: 'Pay via UPI',
                instruments: [{ method: 'upi' }]
              }
            },
            sequence: ['block.upi'],
            hide: [
              { method: 'card' },
              { method: 'netbanking' },
              { method: 'wallet' },
              { method: 'emi' },
              { method: 'paylater' }
            ],
            preferences: { show_default_blocks: false }
          }
        : selectedMethod === 'card'
          ? {
              blocks: {
                card: {
                  name: 'Pay via Card',
                  instruments: [{ method: 'card' }]
                }
              },
              sequence: ['block.card'],
              hide: [
                { method: 'upi' },
                { method: 'netbanking' },
                { method: 'wallet' },
                { method: 'emi' },
                { method: 'paylater' }
              ],
              preferences: { show_default_blocks: false }
            }
          : selectedMethod === 'paylater'
            ? {
                blocks: {
                  paylater: {
                    name: 'Pay Later',
                    instruments: [{ method: 'paylater' }]
                  }
                },
                sequence: ['block.paylater'],
                hide: [
                  { method: 'upi' },
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'wallet' },
                  { method: 'emi' }
                ],
                preferences: { show_default_blocks: false }
              }
            : undefined;

      const options = {
        key: keyId,
        amount,
        currency,
        name: 'Viverra',
        description: `Order #${orderId}`,
        order_id: razorpayOrderId,
        prefill: {
          name: customerName,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          app_order_id: String(orderId)
        },
        method: methodConfig,
        config: displayBlock ? { display: displayBlock } : undefined,
        modal: {
          ondismiss: async () => {
            toast.error('Payment cancelled. You can retry from Order Details.');
            setProcessing(false);
            await fetchCartItems();
            navigate(`/orders/${orderId}`);
          }
        },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API_BASE}/api/payments/razorpay/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                order_id: orderId,
                razorpay_order_id: response?.razorpay_order_id,
                razorpay_payment_id: response?.razorpay_payment_id,
                razorpay_signature: response?.razorpay_signature
              })
            });
            const parsedVerify = await parseJsonResponseSafely(verifyRes);
            const verifyJson = parsedVerify.json;

            if (!verifyRes.ok || !verifyJson?.success) {
              throw new Error(getApiErrorMessage(verifyRes, parsedVerify, 'Payment verification failed'));
            }

            toast.success('Payment verified! Order placed ✅', { duration: 5000 });
            await clearCart();
            navigate(`/orders/${orderId}`);
          } catch (err) {
            console.error('❌ [Checkout] Razorpay verify failed:', err);
            toast.error(err?.message || 'Payment not confirmed. Retry from Order Details.');
            await fetchCartItems();
            navigate(`/orders/${orderId}`);
          } finally {
            setProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', async () => {
        toast.error('Payment failed. Retry from Order Details.');
        setProcessing(false);
        await fetchCartItems();
        navigate(`/orders/${orderId}`);
      });

      rzp.open();
    } catch (error) {
      console.error('❌ [Checkout] Error placing order:', error);
      console.error('❌ [Checkout] Error details:', {
        message: error.message,
        stack: error.stack
      });

      if (createdOrderId) {
        toast.error(error?.message || 'Payment not completed. Continue from Order Details.');
        setProcessing(false);
        navigate(`/orders/${createdOrderId}`);
        return;
      }

      toast.error(error?.message || 'Failed to place order. Please try again.');
      setProcessing(false);
    }
  };

  const subtotal = getCartTotal();
  const freeOver = Number(shippingConfig.freeOver) || 0;
  const flatCharge = Number(shippingConfig.flatCharge) || 0;
  const shipping = !shippingConfig.enabled
    ? 0
    : freeOver > 0 && subtotal >= freeOver
      ? 0
      : Math.max(0, flatCharge);
  const promoDiscount = promoState.applied?.discountAmount ? Number(promoState.applied.discountAmount) : 0;
  const total = Math.max(0, subtotal + shipping - promoDiscount);

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h1>
            <p className="text-gray-600 mb-6">Add at least one item before starting checkout.</p>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Shipping Information */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <TruckIcon className="w-6 h-6 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">Shipping Information</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Saved Addresses Selection */}
                {savedAddresses.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {formData.useNewAddress ? 'Add New Address' : 'Select Saved Address'}
                    </label>
                    {!formData.useNewAddress && (
                      <div className="space-y-3">
                        {savedAddresses.map((address) => (
                          <div
                            key={address.id}
                            onClick={() => handleAddressSelect(address.id)}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.selectedAddressId === address.id
                                ? 'border-indigo-500 bg-indigo-50 shadow-md'
                                : 'border-gray-300 hover:border-indigo-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <input
                                    type="radio"
                                    name="selectedAddress"
                                    checked={formData.selectedAddressId === address.id}
                                    onChange={() => handleAddressSelect(address.id)}
                                    className="w-4 h-4 text-indigo-600"
                                  />
                                  <span className="font-semibold text-gray-900">
                                    {address.first_name} {address.last_name}
                                  </span>
                                  {address.is_default && (
                                    <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full font-semibold">
                                      Default
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 ml-6">
                                  {address.street}
                                </p>
                                <p className="text-sm text-gray-600 ml-6">
                                  {address.city}, {address.state} {address.zip_code}
                                </p>
                                <p className="text-sm text-gray-600 ml-6">
                                  {address.country}
                                </p>
                                {address.phone && (
                                  <p className="text-sm text-gray-500 ml-6 mt-1">
                                    📞 {address.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleNewAddress}
                          className="w-full p-4 border-2 border-gray-300 rounded-xl transition-all text-left hover:border-indigo-300 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">+ Add New Address</span>
                          </div>
                        </button>
                      </div>
                    )}
                    {formData.useNewAddress && (
                      <div className="mb-4">
                        <button
                          type="button"
                          onClick={() => {
                            // Go back to saved addresses
                            const defaultAddress = savedAddresses.find(addr => addr.is_default) || savedAddresses[0];
                            if (defaultAddress) {
                              handleAddressSelect(defaultAddress.id);
                            }
                          }}
                          className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center gap-1"
                        >
                          ← Back to Saved Addresses
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* Address Form Fields - Only show if no saved addresses OR user selected "Add New Address" */}
                {(savedAddresses.length === 0 || formData.useNewAddress) && (
                  <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="123 Main Street"
                  />
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Landmark (Optional)
                  </label>
                  <input
                    type="text"
                    name="landmark"
                    value={formData.landmark}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300"
                    placeholder="Near..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {isIndiaCountry(formData.country) ? 'Locality / Area' : 'City'} <span className="text-red-500">*</span>
                    </label>
                    {isIndiaCountry(formData.country) && pincodeLookup.data?.postOffices?.length ? (
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                          errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Locality</option>
                        {pincodeLookup.data.postOffices.map((po) => (
                          <option key={`${po.name}-${po.block}`} value={po.name}>
                            {po.name}{po.block ? `, ${po.block}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                          errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={isIndiaCountry(formData.country) ? 'Locality' : 'New York'}
                      />
                    )}
                    {errors.city && (
                      <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={isIndiaCountry(formData.country) && !formData.manualLocationEdit}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                        errors.state ? 'border-red-500' : 'border-gray-300'
                      } ${isIndiaCountry(formData.country) && !formData.manualLocationEdit ? 'bg-gray-50' : ''}`}
                      placeholder={isIndiaCountry(formData.country) ? 'Auto-filled' : 'NY'}
                    />
                    {errors.state && (
                      <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Zip Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                        errors.zipCode ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={isIndiaCountry(formData.country) ? '6-digit pincode' : '10001'}
                    />
                    {errors.zipCode && (
                      <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
                    )}
                    {isIndiaCountry(formData.country) && pincodeLookup.loading && (
                      <p className="text-gray-500 text-xs mt-1">Looking up pincode…</p>
                    )}
                    {isIndiaCountry(formData.country) && pincodeLookup.error && (
                      <p className="text-red-500 text-xs mt-1">{pincodeLookup.error}</p>
                    )}
                    {isIndiaCountry(formData.country) && serviceability.message && (
                      <p className="text-amber-700 text-xs mt-1">{serviceability.message}</p>
                    )}
                  </div>
                </div>

                {isIndiaCountry(formData.country) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        District <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        disabled={!formData.manualLocationEdit}
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                          errors.district ? 'border-red-500' : 'border-gray-300'
                        } ${!formData.manualLocationEdit ? 'bg-gray-50' : ''}`}
                        placeholder="Auto-filled"
                      />
                      {errors.district && (
                        <p className="text-red-500 text-sm mt-1">{errors.district}</p>
                      )}
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="manualLocationEdit"
                          checked={formData.manualLocationEdit}
                          onChange={handleChange}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-800">Edit state/district manually</span>
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
                      errors.country ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Country</option>
                    <option value="India">India</option>
                    <option value="United Arab Emirates">United Arab Emirates (UAE)</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Other">Other</option>
                  </select>
                    {errors.country && (
                      <p className="text-red-500 text-sm mt-1">{errors.country}</p>
                    )}
                </div>
                  </div>
                )}

                {/* Payment Method */}
                <div className="bg-gray-50 rounded-xl p-6 mt-8">
                  <div className="flex items-center gap-3 mb-6">
                    <CreditCardIcon className="w-6 h-6 text-indigo-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Payment Method</h2>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Select Payment Method
                    </label>
                    <div className="space-y-3">
                      {availablePaymentMethods.map((method) => {
                        const IconComponent = method.icon;
                        return (
                          <label 
                            key={method.value}
                            className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.paymentMethod === method.value
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-300 hover:border-indigo-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method.value}
                              checked={formData.paymentMethod === method.value}
                              onChange={handleChange}
                              className="mr-3"
                            />
                            <IconComponent className="w-5 h-5 mr-2 text-gray-600" />
                            <span className="font-semibold">{method.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {errors.paymentMethod && (
                      <p className="text-red-500 text-sm mt-2">{errors.paymentMethod}</p>
                    )}
                  </div>

                  {(formData.paymentMethod === 'upi' || formData.paymentMethod === 'card' || formData.paymentMethod === 'paylater') && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <DevicePhoneMobileIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-blue-800 font-semibold text-sm mb-1">
                            {formData.paymentMethod === 'upi'
                              ? 'UPI Payment'
                              : formData.paymentMethod === 'card'
                                ? 'Card Payment'
                                : 'Pay Later'}
                          </p>
                          <p className="text-blue-700 text-xs">
                            {formData.paymentMethod === 'upi'
                              ? 'Clicking “Pay Now” opens the UPI-only Razorpay popup on this page.'
                              : formData.paymentMethod === 'card'
                                ? 'Clicking “Pay Now” opens the card-only Razorpay popup on this page.'
                                : 'Clicking “Pay Now” opens the pay-later-only Razorpay popup on this page.'} Your order is confirmed only after payment is verified.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.paymentMethod === 'cod' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-blue-800 text-sm">
                        💰 You will pay cash when the order is delivered to your address.
                      </p>
                    </div>
                  )}
                </div>

                {/* Save Address Checkbox - Only show if adding new address */}
                {formData.useNewAddress && (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <input
                      type="checkbox"
                      id="saveAddress"
                      name="saveAddress"
                      checked={formData.saveAddress}
                      onChange={handleChange}
                      className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="saveAddress" className="text-sm font-semibold text-blue-800 cursor-pointer">
                      Save this address for future orders {savedAddresses.length === 0 && '(will be set as default)'}
                    </label>
                  </div>
                )}

                {/* Security Notice */}
                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                  <ShieldCheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800 font-semibold text-sm">Secure Checkout</p>
                    <p className="text-green-700 text-xs mt-1">
                      Your payment information is encrypted and secure. We never store your card details.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="w-5 h-5" />
                      {formData.paymentMethod === 'cod' ? 'Place Order' : 'Pay Now'}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="max-h-64 overflow-y-auto space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex items-center gap-3 pb-3 border-b border-gray-100">
                      <img
                        src={item.image_url || '/api/placeholder/60/60'}
                        alt={item.product_name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900 line-clamp-1">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatPrice(item.price)} × {item.quantity}
                        </p>
                      </div>
                      <p className="font-bold text-gray-900">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="pt-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Promo Code
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          setPromoState((prev) => ({ ...prev, error: '' }));
                        }}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter promo code"
                      />
                      <button
                        type="button"
                        disabled={promoState.loading}
                        onClick={async () => {
                          const token = getToken(false);
                          const API_BASE = process.env.REACT_APP_API_URL || '';
                          const code = String(promoCode || '').trim();

                          if (!code) {
                            setPromoState({ loading: false, applied: null, error: '' });
                            return;
                          }

                          if (!token) {
                            toast.error('Please login to apply promo code');
                            return;
                          }

                          setPromoState({ loading: true, applied: null, error: '' });
                          try {
                            const res = await fetch(`${API_BASE}/api/promotions/tiered/validate`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ code })
                            });

                            const contentType = res.headers.get('content-type') || '';
                            let json = null;
                            if (contentType.includes('application/json')) {
                              json = await res.json();
                            } else {
                              const text = await res.text();
                              throw new Error(text ? 'Promo validation failed' : `HTTP ${res.status}`);
                            }

                            if (!res.ok || !json?.success) {
                              throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
                            }

                            const promo = json?.data?.promo;
                            setPromoState({ loading: false, applied: promo, error: '' });
                            toast.success('Promo applied');
                          } catch (err) {
                            setPromoState({ loading: false, applied: null, error: err?.message || 'Invalid promo code' });
                            toast.error(err?.message || 'Invalid promo code');
                          }
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {promoState.loading ? '...' : 'Apply'}
                      </button>
                    </div>

                    {promoState.applied?.code ? (
                      <p className="text-xs text-green-700 mt-2">
                        Applied <span className="font-semibold">{promoState.applied.code}</span>
                        {promoState.applied.summaryText
                          ? ` - ${promoState.applied.summaryText}`
                          : promoState.applied.discountPercent
                            ? ` (${promoState.applied.discountPercent}% off)`
                            : ''}
                      </p>
                    ) : promoState.error ? (
                      <p className="text-xs text-red-600 mt-2">{promoState.error}</p>
                    ) : null}
                  </div>

                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-semibold">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Shipping:</span>
                    <span className="font-semibold">
                      {shipping === 0 ? (
                        <span className="text-green-600">Free</span>
                      ) : (
                        formatPrice(shipping)
                      )}
                    </span>
                  </div>

                  {promoDiscount > 0 ? (
                    <div className="flex justify-between text-gray-700">
                      <span>Discount:</span>
                      <span className="font-semibold text-green-700">- {formatPrice(promoDiscount)}</span>
                    </div>
                  ) : null}

                  <div className="border-t-2 border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-900">Total:</span>
                      <span className="text-2xl font-bold text-indigo-600">{formatPrice(total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
